import {
  FileObject,
  OptionalFields,
  OptionalFieldsP21Case,
  P21ProcessorResult,
  transformCases,
} from '@bindoc-projects/kost-lib-multi';
import {
  KostDataImportArchiveHelper,
  KostDataImportDatasetInputProcessor,
  KostDataImportDatasetInputProcessorProgressHandler,
  KostDataImportDatasetInputProcessorResult,
} from '@bindoc-projects/kost-data-import-domain-lib';
import { KostFileExtractor } from './file-extractor';
import JSZip from 'jszip';
import { P21FileParser } from './file-parser';
import { KostFileNames } from './de-kost-file.const';
import {
  AysFabCreationStrategy,
  AysVirtualClinicP21ImportFabSelectionStrategy,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { BdState } from '@bindoc-projects/geographic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import { AysDrgBaseRate } from '@bindoc-projects/code-domain-lib';

/** Steps of the processing in order, as the progress view lists them. */
export const KOST_DATA_IMPORT_DE_INPUT_STEPS = {
  ARCHIVE_READING: 'Archiv entpacken',
  FILE_EXTRACTING: 'Dateien extrahieren',
  FILE_PARSING: 'Datei einlesen',
  CASE_READING: 'Fälle einlesen',
  CASE_TRANSFORMATION: 'Fälle transformieren',
  ARCHIVE_CREATING: 'Archiv packen',
};
export class KostDataImportDatasetInputProcessorDe extends KostDataImportDatasetInputProcessor {
  constructor() {
    super(
      new KostDataImportDatasetInputProcessorProgressHandler(
        KOST_DATA_IMPORT_DE_INPUT_STEPS,
      ),
    );
  }

  public async process(
    inputFile: File,
    parameters: {
      data: {
        fabSelectionStrategy: AysVirtualClinicP21ImportFabSelectionStrategy;
        fabCreationStrategy: AysFabCreationStrategy;
        state: BdState;
        baseRates: { [year: string]: number };
      };
      anonymization: {
        salt: string;
        p21Case: {
          postalCode: boolean;
          randomizeInternMark: boolean;
        };
      };
    },
    dependencies: {
      postalCodeAdministrativeDistrictIdMap: { [postalCode: string]: number };
      drgBaseRates: { [year: string]: AysDrgBaseRate[] };
    },
  ): Promise<KostDataImportDatasetInputProcessorResult> {
    this.progressHandler.update({
      step: 'ARCHIVE_READING',
      current: 0,
      max: 1,
    });
    const zipContentResult = await KostDataImportArchiveHelper.unzip(
      inputFile,
      (event) => {
        this.progressHandler.update(event);
      },
    );

    if (
      zipContentResult.errors &&
      (zipContentResult.errors as string[]).length > 0
    ) {
      return Promise.resolve(zipContentResult);
    }

    const p21FilesResult = await KostFileExtractor.extract(
      zipContentResult as {
        [fileType in KostFileNames]: JSZip.JSZipObject;
      },
      (event) => {
        this.progressHandler.update(event);
      },
    );

    if (p21FilesResult.errors.length > 0) {
      return Promise.resolve(p21FilesResult);
    }

    const parsedResult = P21FileParser.parse(p21FilesResult.files, (event) => {
      this.progressHandler.update(event);
    });
    if (parsedResult.errors && parsedResult.errors.length > 0) {
      return Promise.resolve(parsedResult);
    }

    const fileObject = new FileObject(
      parsedResult.result.FALL,
      parsedResult.result.ENTGELTE,
      parsedResult.result.FAB,
      parsedResult.result.ICD,
      parsedResult.result.OPS,
      parsedResult.result.KOSTEN,
    );

    console.log(parameters.data.baseRates);

    const transformedCases: P21ProcessorResult<unknown[]> =
      await transformCases(
        fileObject,
        new OptionalFields(
          new OptionalFieldsP21Case(
            parameters.anonymization.p21Case.randomizeInternMark,
            parameters.anonymization.p21Case.postalCode,
          ),
        ),
        parameters.data.baseRates,
        dependencies.postalCodeAdministrativeDistrictIdMap,
        (event) => {
          this.progressHandler.update(event);
        },
        parameters.anonymization.salt,
      );

    if (transformedCases.errors && transformedCases.errors.length > 0) {
      const errorResult: KostDataImportDatasetInputProcessorResult = {
        errors: transformedCases.errors,
        anonymizationTable: transformedCases.anonymizationTable,
        locations: {},
        additionalInformation: transformedCases.additionalInformation,
      };
      return Promise.resolve(errorResult);
    }

    this.progressHandler.update({
      step: 'ARCHIVE_CREATING',
      current: 0,
      max: 1,
    });
    return {
      transformed: await this._packTransformedCases(transformedCases),
      anonymizationTable: transformedCases.anonymizationTable,
      additionalInformation: transformedCases.additionalInformation,
      locations: transformedCases.clinicIds,
    };
  }

  private async _packTransformedCases(
    processorResult: P21ProcessorResult<unknown[]>,
    chunkSize = 20_000,
  ): Promise<Blob> {
    const zip = new JSZip();
    const chunkedResult = BdArrayUtil.chunking(
      processorResult.data.cases,
      chunkSize,
    );

    chunkedResult.forEach((chunk, index) => {
      const fileName = index === 0 ? 'data.json' : `data_${index}.json`;
      const fileContent: {
        data: unknown[];
        errors: string[];
      } = {
        data: chunk,
        errors: [], // just upload the data value, errors should be empty
      };

      zip.file(fileName, JSON.stringify(fileContent));
    });

    const archive = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 8,
      },
    });

    this.progressHandler.update({
      step: 'Erstellen der neuen ZIP-Datei abgeschlossen',
      current: 1,
      max: 1,
      type: { name$: 'STEP_UPDATE' },
    });

    return archive;
  }
}
